import { useState } from 'react';
import { attendanceTabBySlug, type AttendanceTab } from '../../constants/attendanceTabs';
import PresenceBoard from './PresenceBoard';
import TimelineTab from './TimelineTab';
import EmployeesTab from './EmployeesTab';
import UnknownFacesTab from './UnknownFacesTab';
import PayrollTab from './PayrollTab';
import AttendanceConfigPage from './AttendanceConfigPage';
import EmployeeDetail from './EmployeeDetail';

export interface AttendancePageProps {
  /** Which surface to render — the `:tab` route segment. */
  slug?: string;
  /** Changes when the operator switches node (cloud); forces a refetch. */
  nodeKey?: string;
  /**
   * Rendered instead of the surface when the host app has no node selected
   * (cloud with an empty node dropdown). Local never passes this.
   */
  emptyState?: React.ReactNode;
}

/**
 * One page per attendance surface — the tab bar became sidebar sub-menu entries,
 * so this shell just resolves the slug and owns the employee drawer (which any
 * surface can open).
 */
export default function AttendancePage({ slug, nodeKey = '', emptyState }: AttendancePageProps) {
  const [openEmployee, setOpenEmployee] = useState<string | null>(null);
  const tab: AttendanceTab | undefined = attendanceTabBySlug(slug);

  if (emptyState) return <>{emptyState}</>;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-foreground">{tab?.label || 'Attendance'}</h1>

      {slug === 'presence' && <PresenceBoard nodeKey={nodeKey} onOpenEmployee={setOpenEmployee} />}
      {slug === 'timeline' && <TimelineTab nodeKey={nodeKey} />}
      {slug === 'employees' && <EmployeesTab nodeKey={nodeKey} onOpenEmployee={setOpenEmployee} />}
      {slug === 'unknown-faces' && <UnknownFacesTab nodeKey={nodeKey} />}
      {slug === 'payroll' && <PayrollTab nodeKey={nodeKey} onOpenEmployee={setOpenEmployee} />}
      {slug === 'config' && <AttendanceConfigPage nodeKey={nodeKey} />}

      {openEmployee && <EmployeeDetail employeeId={openEmployee} onClose={() => setOpenEmployee(null)} />}
    </div>
  );
}
